/**
 * Shared master bus — level, protect, hiss only.
 * Channel character stays in per-channel voice + FX graphs.
 */
import * as Tone from 'tone';

const MASTER_LEVEL = 0.82;
const STORAGE_MASTER = 'nt-master-level';

let toneChain = null;
let hissNodes = null;
let memoryLevel = MASTER_LEVEL;

function readStoredLevel() {
  try {
    const v = parseFloat(sessionStorage.getItem(STORAGE_MASTER));
    return Number.isFinite(v) ? v : MASTER_LEVEL;
  } catch (_) {
    // sessionStorage can throw (Safari "Block All Cookies", some in-app
    // webviews, privacy extensions) — never let that break the audio bus.
    return memoryLevel;
  }
}

function storeLevel(v) {
  memoryLevel = v;
  try {
    sessionStorage.setItem(STORAGE_MASTER, String(v));
  } catch (_) {
    /* storage blocked — memoryLevel already holds the value for this session */
  }
}

export function getToneInput() {
  if (!toneChain) {
    const stored = readStoredLevel();
    const input = new Tone.Gain(stored < 0.15 ? MASTER_LEVEL : stored);
    const comp = new Tone.Compressor({
      threshold: -22, ratio: 5, attack: 0.006, release: 0.32,
    });
    const limiter = new Tone.Limiter(-4);
    input.chain(comp, limiter, Tone.getDestination());

    const hissNoise = new Tone.Noise('white');
    const hissHP = new Tone.Filter({ frequency: 4400, type: 'highpass' });
    const hissGain = new Tone.Gain(0.0018);
    hissNoise.chain(hissHP, hissGain, Tone.getDestination());
    hissNoise.start();
    hissNodes = { hissNoise, hissHP, hissGain };

    toneChain = { input, comp, limiter };
  }
  return toneChain.input;
}

export function connectTone(node) {
  const input = getToneInput();
  if (input && node) node.connect(input);
  return input;
}

export async function ensureAudio() {
  await Tone.start();
  const ctx = Tone.getContext?.() || Tone.context;
  if (ctx && typeof ctx.resume === 'function') await ctx.resume();
  else if (ctx?.rawContext?.resume) await ctx.rawContext.resume();
  getToneInput();
}

export function fadeMaster(target, durationMs = 120) {
  if (!toneChain) return;
  const now = Tone.now();
  const g = toneChain.input.gain;
  g.cancelScheduledValues(now);
  g.setValueAtTime(g.value, now);
  g.linearRampToValueAtTime(Math.max(0.0001, target), now + durationMs / 1000);
}

export function fadeOutMaster(ms = 120) {
  return new Promise((resolve) => {
    fadeMaster(0.0001, ms);
    setTimeout(resolve, ms + 30);
  });
}

export function fadeInMaster(ms = 180) {
  fadeMaster(readStoredLevel(), ms);
}

/** Resume audio when returning to the tab. Do not suspend on hide —
 *  desktop should keep playing in the background (legacy audio-bus behavior). */
export function resumeWhenVisible() {
  if (document.hidden) return;
  try {
    const ctx = typeof Tone.getContext === 'function' ? Tone.getContext() : Tone.context;
    const raw = ctx?.rawContext;
    const state = raw?.state || ctx?.state;
    if (state !== 'suspended') return;
    if (typeof ctx.resume === 'function') ctx.resume();
    else raw?.resume?.();
  } catch (_) {
    /* ignore — never break channel UX on tab visibility */
  }
}

export const bus = {
  MASTER_LEVEL,
  getToneInput,
  connectTone,
  ensureAudio,
  fadeOutMaster,
  fadeInMaster,
  readStoredLevel,
  storeLevel,
};

// Dev leak helper — count Tone nodes loosely via context destination connections
export function assertBusHealthy(label = '') {
  if (!import.meta.env.DEV || !toneChain) return;
  // eslint-disable-next-line no-console
  console.debug(`[nt-bus] ${label}`, {
    contextState: (Tone.getContext?.() || Tone.context)?.rawContext?.state
      || (Tone.getContext?.() || Tone.context)?.state,
    masterGain: toneChain.input.gain.value,
  });
}

document.addEventListener('visibilitychange', resumeWhenVisible);

export default bus;
